import { useCallback, useMemo } from "react";
import { Stack } from "@mui/material";
import { Task, TaskType } from "@/resources/types";
import PatientTaskButton from "@/components/PatientTaskButton";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";

// Task definition types (matching the settings structure)
type TaskState = {
  id: string;
  name: string;
  colour: string;
  order: number;
};

type TaskDefinition = {
  id: string;
  name: string;
  icon: string;
  states: TaskState[];
};

export type PatientTaskModes = "simple" | "condensed";

interface PatientTasksProps {
  value: Task[];
  onChange: (tasks: Task[]) => void;
  mode?: PatientTaskModes;
  disabled?: boolean;
}

const getTask = (type: TaskType, tasks: Task[]): Task => tasks.find((t) => t.type === type) ?? { type, status: "not_requested" };

export const PatientTasks = ({ value, onChange, mode, disabled }: PatientTasksProps) => {
  const { settings } = useSettingsContext();

  // Get task definitions from settings
  const taskDefinitions: TaskDefinition[] = (settings as any)?.taskDefinitions || [];

  // If no task definitions, fall back to old behavior
  if (taskDefinitions.length === 0) {
    const handleTaskChange = useCallback((task: Task) => {
      // ensures all task types are present
      const newTasks = Object.values(TaskType).map((type) => {
        const t = getTask(type, value);
        return t.type === task.type ? task : t;
      });
      onChange(newTasks);
    }, [value]);

    const taskButtons = useMemo(() => Object.values(TaskType).map((type) => {
      const task = getTask(type, value);
      return (
        <PatientTaskButton disabled={disabled} key={task.type} value={task} onClick={handleTaskChange} small={mode === "condensed"} />
      );
    }), [value]);

    return (
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {taskButtons}
      </Stack>
    );
  }

  // New behavior with task definitions
  const handleTaskChange = useCallback((task: Task) => {
    // Find the task definition for this task
    const taskDef = taskDefinitions.find(td => td.name === task.type);
    if (!taskDef) return;

    // Update the task in the list
    const newTasks = value.map(t => t.type === task.type ? task : t);

    // Ensure all task definitions have corresponding tasks
    const allTasks = taskDefinitions.map(td => {
      const existingTask = newTasks.find(t => t.type === td.name);
      return existingTask || { type: td.name as TaskType, status: "not_requested" as const };
    });

    onChange(allTasks);
  }, [value, taskDefinitions, onChange]);

  const taskButtons = useMemo(() => taskDefinitions.map((taskDef) => {
    const task = value.find(t => t.type === taskDef.name) || {
      type: taskDef.name as TaskType,
      status: "not_requested" as const
    };
    return (
      <PatientTaskButton
        disabled={disabled}
        key={taskDef.id}
        value={task}
        onClick={handleTaskChange}
        small={mode === "condensed"}
        taskDefinition={taskDef}
      />
    );
  }), [value, taskDefinitions, handleTaskChange, disabled, mode]);

  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {taskButtons}
    </Stack>
  );
};